import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Users, CheckSquare, Clock, AlertTriangle, Calendar, TrendingUp, ChevronRight, RefreshCw, UserX, ArrowRight, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attDate, setAttDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/manager/team-dashboard`, { withCredentials: true });
      setDashboard(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExceptions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/manager/exceptions`, { withCredentials: true });
      setExceptions(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchExceptions();
  }, [fetchDashboard, fetchExceptions]);

  const fetchTeamAttendance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/manager/team-attendance?attendance_date=${attDate}`, { withCredentials: true });
      setTeamAttendance(data || []);
    } catch (e) {
      console.error(e);
    }
  }, [attDate]);

  useEffect(() => {
    fetchTeamAttendance();
  }, [attDate, fetchTeamAttendance]);

  const approvalItems = dashboard?.pending_approvals || {};
  const attToday = dashboard?.attendance_today || {};
  const teamSize = dashboard?.team_size || 0;

  const attColor = (s) => {
    const st = (s || '').toLowerCase();
    if (st.includes('present')) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (st.includes('absent')) return 'text-red-700 bg-red-50 border-red-200';
    if (st.includes('leave')) return 'text-blue-700 bg-blue-50 border-blue-200';
    if (st.includes('half')) return 'text-amber-700 bg-amber-50 border-amber-200';
    if (st.includes('week') || st.includes('off') || st.includes('holiday')) return 'text-slate-600 bg-slate-100 border-slate-200';
    return 'text-slate-500 bg-slate-50 border-slate-200';
  };

  return (
    <MainLayout>
      <div data-testid="manager-dashboard-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl font-bold text-slate-900">Manager Dashboard</h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-[#0052FF] border border-blue-200">
                Manager View
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-1">Real-time team reporting, attendance tracking, and pending approvals</p>
          </div>
          <button
            onClick={() => { setLoading(true); fetchDashboard(); fetchTeamAttendance(); fetchExceptions(); }}
            className="flex items-center space-x-2 border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Empty State when no team is assigned */}
        {teamSize === 0 && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
              <UserX className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-amber-900">No employees are currently assigned to you.</h3>
            <p className="text-sm text-amber-700 max-w-lg mx-auto">
              You are currently logged in as a Reporting Manager, but no active employees have been mapped under your reporting hierarchy in Employee Master.
            </p>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center space-x-2 mb-2">
              <Users className="w-5 h-5 text-[#0052FF]" />
              <p className="text-xs font-medium text-slate-500">Total Team Size</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">{teamSize}</p>
          </div>

          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 shadow-sm">
            <div className="flex items-center space-x-2 mb-2">
              <CheckSquare className="w-5 h-5 text-emerald-600" />
              <p className="text-xs font-medium text-emerald-700">Present Today</p>
            </div>
            <p className="text-2xl font-bold text-emerald-800">{attToday.present || 0}</p>
          </div>

          <div className="bg-red-50 rounded-xl border border-red-200 p-4 shadow-sm">
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <p className="text-xs font-medium text-red-700">Absent / On Leave</p>
            </div>
            <p className="text-2xl font-bold text-red-800">{(attToday.absent || 0) + (attToday.on_leave || 0)}</p>
          </div>

          <div
            className="bg-amber-50 rounded-xl border border-amber-200 p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/manager/approvals')}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <p className="text-xs font-medium text-amber-700">Pending Approvals</p>
            </div>
            <p className="text-2xl font-bold text-amber-800">{approvalItems.total || 0}</p>
          </div>
        </div>

        {/* Pending Approvals Breakdown */}
        {approvalItems.total > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Pending Approvals Inbox</h3>
                <p className="text-xs text-slate-500">Tasks awaiting your decision</p>
              </div>
              <button
                data-testid="go-to-approvals"
                onClick={() => navigate('/manager/approvals')}
                className="text-xs font-medium text-[#0052FF] hover:underline flex items-center space-x-1"
              >
                <span>Open Approval Inbox</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {[
                { key: 'leave', label: 'Leave Applications', color: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
                { key: 'permission', label: 'Permission', color: 'bg-teal-50 text-teal-800 border-teal-200' },
                { key: 'regularization', label: 'Regularizations', color: 'bg-blue-50 text-blue-800 border-blue-200' },
                { key: 'ot', label: 'Overtime (OT)', color: 'bg-purple-50 text-purple-800 border-purple-200' },
                { key: 'shift_request', label: 'Shift Requests', color: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
                { key: 'compoff', label: 'Comp-Off Requests', color: 'bg-pink-50 text-pink-800 border-pink-200' },
                { key: 'travel', label: 'Travel Requests', color: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
                { key: 'local_conveyance', label: 'Local Conveyance', color: 'bg-sky-50 text-sky-800 border-sky-200' },
                { key: 'outstation_claim', label: 'Outstation Claims', color: 'bg-[#0052FF]/10 text-[#0052FF] border-[#0052FF]/20' },
                { key: 'advance', label: 'Advance Requests', color: 'bg-amber-50 text-amber-800 border-amber-200' },
                { key: 'advance_settlement', label: 'Advance Settlements', color: 'bg-orange-50 text-orange-800 border-orange-200' },
                { key: 'reimbursement', label: 'Reimbursements', color: 'bg-slate-50 text-slate-800 border-slate-200' },
              ].map(({ key, label, color }) => (
                <div
                  key={key}
                  onClick={() => navigate('/manager/approvals')}
                  className={`rounded-lg border p-3 cursor-pointer hover:opacity-90 transition-opacity ${color}`}
                >
                  <p className="text-xl font-bold">{approvalItems[key] || 0}</p>
                  <p className="text-xs font-medium truncate mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Attendance */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Team Attendance</h3>
              <p className="text-xs text-slate-500">View daily attendance status for your reporting team</p>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <input
                type="date"
                data-testid="att-date-input"
                value={attDate}
                onChange={(e) => setAttDate(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-700">
              <thead className="bg-slate-100/70 text-slate-600 font-semibold uppercase text-xs tracking-wider border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Department</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Check-In</th>
                  <th className="px-4 py-3">Check-Out</th>
                  <th className="px-4 py-3">Shift</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {teamAttendance.length > 0 ? (
                  teamAttendance.map((a, i) => (
                    <tr key={i} data-testid={`team-att-${i}`} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-900">{a.employee_name || '—'}</p>
                        <p className="text-xs text-slate-500">{a.employee_code || a.employee_id}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{a.department || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{a.designation || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${attColor(a.status)}`}>
                          {a.status || 'not marked'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{a.check_in || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{a.check_out || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{a.shift || 'General'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-sm text-slate-400 py-8 text-center">
                      No team attendance records found for {attDate}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Exceptions */}
        {exceptions.length > 0 && (
          <div className="bg-white rounded-xl border border-red-200 overflow-hidden shadow-sm">
            <div className="p-4 border-b border-red-100 bg-red-50/80 flex items-center justify-between">
              <h3 className="text-sm font-bold text-red-800 flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span>Unresolved Exceptions ({exceptions.length})</span>
              </h3>
            </div>
            <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
              {exceptions.map((e, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-red-50/30">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{e.exception_type}: {e.message || e.description}</p>
                    <p className="text-xs text-slate-500">Employee: {e.employee_id} &middot; Period: {e.period}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ManagerDashboard;
